import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { CATEGORIES } from '../constants';
import type { Plant } from '../types';

type DirectionMode = 'de-to-bot' | 'bot-to-de' | 'both';
type CardDirection = 'de-to-bot' | 'bot-to-de';
type SessionPhase = 'setup' | 'active' | 'feedback' | 'results';

interface LearnCard {
  plant: Plant;
  direction: CardDirection;
}

interface WrongAnswer {
  plant: Plant;
  userAnswer: string;
  correctAnswer: string;
  direction: CardDirection;
}

interface SessionStats {
  correct: number;
  wrong: number;
  streak: number;
  bestStreak: number;
  wrongAnswers: WrongAnswer[];
  perPlant: Record<string, { correct: number; wrong: number }>;
}

interface PendingFeedback {
  card: LearnCard;
  userAnswer: string;
  correctAnswer: string;
  distance: number;
}

const initialStats: SessionStats = {
  correct: 0,
  wrong: 0,
  streak: 0,
  bestStreak: 0,
  wrongAnswers: [],
  perPlant: {},
};

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function normalizeAnswer(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(a: string, b: string): number {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix = Array.from({ length: rows }, () => Array<number>(cols).fill(0));

  for (let i = 0; i < rows; i++) matrix[i][0] = i;
  for (let j = 0; j < cols; j++) matrix[0][j] = j;

  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[a.length][b.length];
}

function makeCard(plant: Plant, mode: DirectionMode): LearnCard {
  const direction = mode === 'both'
    ? (Math.random() > 0.5 ? 'de-to-bot' : 'bot-to-de')
    : mode;
  return { plant, direction };
}

function questionLabel(direction: CardDirection): string {
  return direction === 'de-to-bot' ? 'Deutscher Name' : 'Botanischer Name';
}

function answerLabel(direction: CardDirection): string {
  return direction === 'de-to-bot' ? 'Botanischer Name' : 'Deutscher Name';
}

function questionFor(card: LearnCard): string {
  return card.direction === 'de-to-bot' ? card.plant.german_name : card.plant.botanical_name;
}

function answerFor(card: LearnCard): string {
  return card.direction === 'de-to-bot' ? card.plant.botanical_name : card.plant.german_name;
}

export const LearnPage: React.FC = () => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [category, setCategory] = useState('Alle');
  const [directionMode, setDirectionMode] = useState<DirectionMode>('de-to-bot');
  const [phase, setPhase] = useState<SessionPhase>('setup');
  const [customLimit, setCustomLimit] = useState('');
  const [queue, setQueue] = useState<LearnCard[]>([]);
  const [current, setCurrent] = useState<LearnCard | null>(null);
  const [answer, setAnswer] = useState('');
  const [stats, setStats] = useState<SessionStats>(initialStats);
  const [pendingFeedback, setPendingFeedback] = useState<PendingFeedback | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [streakPulse, setStreakPulse] = useState(false);
  const answerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.getPlants()
      .then(setPlants)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (phase === 'active') {
      answerRef.current?.focus();
    }
  }, [phase, current]);

  // Filter tabItems to only show categories with at least 1 plant
  const tabItems = useMemo(() => {
    const counts: Record<string, number> = { Alle: plants.length };
    for (const cat of CATEGORIES) {
      counts[cat] = plants.filter((p) => p.category === cat).length;
    }
    return ['Alle', ...CATEGORIES.filter((cat) => counts[cat] > 0)];
  }, [plants]);

  const eligiblePlants = useMemo(() => {
    return category === 'Alle' ? plants : plants.filter((plant) => plant.category === category);
  }, [category, plants]);

  const totalAttempts = stats.correct + stats.wrong;
  const score = totalAttempts > 0 ? Math.round((stats.correct / totalAttempts) * 100) : 0;

  const startSession = (sourcePlants: Plant[], limit?: number) => {
    const picked = shuffle(sourcePlants).slice(0, limit ?? sourcePlants.length);
    const cards = picked.map((plant) => makeCard(plant, directionMode));
    setStats(initialStats);
    setQueue(cards.slice(1));
    setCurrent(cards[0] ?? null);
    setAnswer('');
    setPendingFeedback(null);
    setPhase(cards.length > 0 ? 'active' : 'setup');
  };

  const finishOrAdvance = (extraCards: LearnCard[] = []) => {
    setQueue((prev) => {
      const nextQueue = [...prev, ...extraCards];
      const [nextCard, ...rest] = nextQueue;
      setCurrent(nextCard ?? null);
      setAnswer('');
      setPendingFeedback(null);
      setPhase(nextCard ? 'active' : 'results');
      return rest;
    });
  };

  const recordAnswer = (card: LearnCard, userAnswer: string, isCorrect: boolean, requeueWrong: boolean) => {
    const correctAnswer = answerFor(card);
    const plantId = card.plant.id;
    const requeuedCards = requeueWrong
      ? [makeCard(card.plant, directionMode), makeCard(card.plant, directionMode)]
      : [];

    setStats((prev) => {
      const nextStreak = isCorrect ? prev.streak + 1 : 0;
      return {
        correct: prev.correct + (isCorrect ? 1 : 0),
        wrong: prev.wrong + (isCorrect ? 0 : 1),
        streak: nextStreak,
        bestStreak: Math.max(prev.bestStreak, nextStreak),
        wrongAnswers: isCorrect
          ? prev.wrongAnswers
          : [...prev.wrongAnswers, { plant: card.plant, userAnswer, correctAnswer, direction: card.direction }],
        perPlant: {
          ...prev.perPlant,
          [plantId]: {
            correct: (prev.perPlant[plantId]?.correct ?? 0) + (isCorrect ? 1 : 0),
            wrong: (prev.perPlant[plantId]?.wrong ?? 0) + (isCorrect ? 0 : 1),
          },
        },
      };
    });

    if (isCorrect) {
      setStreakPulse(true);
      window.setTimeout(() => setStreakPulse(false), 450);
    }

    finishOrAdvance(requeuedCards);
  };

  const submitAnswer = (event: React.FormEvent) => {
    event.preventDefault();
    if (!current) return;

    const normalizedUser = normalizeAnswer(answer);
    const normalizedCorrect = normalizeAnswer(answerFor(current));
    const distance = levenshtein(normalizedUser, normalizedCorrect);

    if (normalizedUser.length > 0 && normalizedUser === normalizedCorrect) {
      recordAnswer(current, answer, true, false);
      return;
    }

    setPendingFeedback({
      card: current,
      userAnswer: answer,
      correctAnswer: answerFor(current),
      distance,
    });
    setPhase('feedback');
  };

  const markPending = (isCorrect: boolean) => {
    if (!pendingFeedback) return;
    recordAnswer(pendingFeedback.card, pendingFeedback.userAnswer, isCorrect, !isCorrect);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5">
        <div className="w-14 h-14 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20 animate-pulse">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="text-sage">
            <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
          </svg>
        </div>
        <p className="text-creamDim text-sm font-mono">Lernmodus wird geladen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <div className="panel p-8 max-w-md text-center flex flex-col gap-4 shadow-cardHover">
          <p className="plant-name text-xl text-cream font-semibold">Verbindungsfehler</p>
          <p className="text-creamDim text-sm">{error}</p>
          <button onClick={() => window.location.reload()} className="btn-primary mx-auto">
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (plants.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
        <div className="panel p-10 max-w-sm text-center flex flex-col items-center gap-5 shadow-cardHover">
          <h1 className="plant-name text-2xl font-bold text-cream">Noch keine Pflanzen</h1>
          <p className="text-creamDim text-sm">Fuege deine ersten Pflanzenkarten im Adminbereich hinzu.</p>
          <Link to="/admin" className="btn-primary">Pflanze hinzufuegen</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-10 nav-glass">
        <div className="flex items-center justify-between px-5 py-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-sage/15 flex items-center justify-center border border-sage/20">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-sage">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 008 20C19 20 22 3 22 3c-1 2-8 3.5-13.5 7"/>
              </svg>
            </div>
            <span className="plant-name text-cream font-semibold text-lg">Pflanzenkarten</span>
          </Link>
          <div className="flex items-center gap-2">
            {phase !== 'setup' && (
              <span className={`text-sm font-mono px-3 py-1 rounded-full border transition-all duration-200 ${streakPulse ? 'streak-pop bg-goldLight text-sage border-gold' : 'bg-sage/10 text-sage border-sage/20'}`}>
                🔥 {stats.streak}
              </span>
            )}
            <Link to="/" className="btn-ghost text-xs">Karten</Link>
            <Link to="/admin" className="btn-ghost text-xs">Admin</Link>
          </div>
        </div>

        <div className="flex gap-1.5 px-5 pb-2.5 overflow-x-auto scrollbar-none">
          {tabItems.map((cat) => {
            const count = cat === 'Alle' ? plants.length : plants.filter((plant) => plant.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => phase === 'setup' && setCategory(cat)}
                disabled={phase !== 'setup'}
                className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all duration-200 disabled:opacity-50
                  ${category === cat
                    ? 'bg-sage text-white border-sage'
                    : 'bg-white/70 text-creamDim border-border/60 hover:border-sage/50 hover:text-sage'}`}
              >
                {cat}
                <span className={`text-[10px] px-1 rounded-full font-mono ${category === cat ? 'bg-white/20' : 'bg-border/40'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      <main className="min-h-screen pt-32 px-4 pb-10">
        {phase === 'setup' && (
          <section className="max-w-3xl mx-auto flex flex-col gap-5">
            <div className="panel-paper p-6 md:p-8 shadow-cardHover">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-sage mb-2">Lernmodus</p>
                  <h1 className="plant-name text-3xl md:text-4xl font-bold text-cream">Antwort eintippen</h1>
                  <p className="text-creamDim text-sm mt-2 max-w-xl">
                    Kategorie waehlen, Richtung festlegen und dann eine kurze Zufallsrunde oder alle Karten trainieren.
                  </p>
                </div>
                <span className="tag self-start">{eligiblePlants.length} Karten verfuegbar</span>
              </div>
            </div>

            <div className="panel p-5 md:p-6 flex flex-col gap-4">
              <p className="text-sm font-semibold text-cream">Richtung</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  { key: 'de-to-bot' as DirectionMode, label: 'Deutsch -> Botanisch' },
                  { key: 'bot-to-de' as DirectionMode, label: 'Botanisch -> Deutsch' },
                  { key: 'both' as DirectionMode, label: 'Beides' },
                ].map((option) => (
                  <button
                    key={option.key}
                    onClick={() => setDirectionMode(option.key)}
                    className={`px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-200 text-left
                      ${directionMode === option.key
                        ? 'bg-sage text-white border-sage shadow-btn'
                        : 'bg-white text-creamDim border-border hover:border-sage/50 hover:text-sage'}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="panel p-5 md:p-6 flex flex-col gap-4">
              <p className="text-sm font-semibold text-cream">Anzahl der Karten</p>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  min="1"
                  max={eligiblePlants.length}
                  placeholder="z.B. 30"
                  value={customLimit}
                  onChange={(e) => setCustomLimit(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-border bg-white text-creamDim text-sm flex-1"
                />
                <button
                  onClick={() => {
                    const num = Math.min(parseInt(customLimit) || 1, eligiblePlants.length);
                    startSession(eligiblePlants, num);
                  }}
                  disabled={eligiblePlants.length === 0 || !customLimit}
                  className="btn-primary px-4 py-2 text-sm"
                >
                  Custom starten
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                onClick={() => startSession(eligiblePlants, 50)}
                disabled={eligiblePlants.length === 0}
                className="btn-primary justify-center py-4"
              >
                50 Karten starten
              </button>
              <button
                onClick={() => startSession(eligiblePlants)}
                disabled={eligiblePlants.length === 0}
                className="btn-ghost justify-center py-4 bg-white/80"
              >
                Alle Pflanzen starten
              </button>
            </div>
          </section>
        )}

        {(phase === 'active' || phase === 'feedback') && current && (
          <section className="max-w-3xl mx-auto flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-xs text-creamDim bg-white/70 px-3 py-1 rounded-full border border-border/60">
                Noch {queue.length + 1} in der Runde
              </span>
              <span className="font-mono text-xs text-creamDim bg-white/70 px-3 py-1 rounded-full border border-border/60">
                Best: {stats.bestStreak}
              </span>
            </div>

            <div className="panel overflow-hidden shadow-cardHover">
              <div className="aspect-[4/3] md:aspect-[16/9] bg-paper">
                {current.plant.image_url ? (
                  <img src={current.plant.image_url} alt={current.plant.german_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-creamDim">Kein Bild vorhanden</div>
                )}
              </div>
              <div className="p-5 md:p-7 flex flex-col gap-5">
                <div>
                  <p className="text-xs font-mono uppercase tracking-wider text-creamDim mb-1">{questionLabel(current.direction)}</p>
                  <h1 className={`text-3xl md:text-4xl font-bold text-cream leading-tight ${current.direction === 'bot-to-de' ? 'botanical' : 'plant-name'}`}>
                    {questionFor(current)}
                  </h1>
                  <span className="tag mt-3">{current.plant.category}</span>
                </div>

                {phase === 'active' && (
                  <form onSubmit={submitAnswer} className="flex flex-col gap-3">
                    <label className="text-sm font-semibold text-cream" htmlFor="learn-answer">
                      {answerLabel(current.direction)}
                    </label>
                    <input
                      ref={answerRef}
                      id="learn-answer"
                      value={answer}
                      onChange={(event) => setAnswer(event.target.value)}
                      className="input text-base"
                      autoComplete="off"
                      placeholder="Antwort eingeben..."
                    />
                    <button type="submit" className="btn-primary justify-center" disabled={answer.trim().length === 0}>
                      Pruefen
                    </button>
                  </form>
                )}

                {phase === 'feedback' && pendingFeedback && (
                  <div className="flex flex-col gap-4 animate-fadeIn">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <p className="text-xs font-mono uppercase tracking-wider text-amber-700 mb-2">Deine Antwort</p>
                        <p className="text-cream font-semibold break-words">{pendingFeedback.userAnswer || '-'}</p>
                      </div>
                      <div className="rounded-xl border border-sage/20 bg-sage/10 p-4">
                        <p className="text-xs font-mono uppercase tracking-wider text-sage mb-2">Richtige Antwort</p>
                        <p className="text-cream font-semibold break-words">{pendingFeedback.correctAnswer}</p>
                      </div>
                    </div>
                    {pendingFeedback.distance <= 2 && (
                      <p className="text-sm text-creamDim">Fast: Die Antwort liegt innerhalb von {pendingFeedback.distance} Zeichen.</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <button onClick={() => markPending(true)} className="btn-primary justify-center">
                        Richtig werten
                      </button>
                      <button onClick={() => markPending(false)} className="btn-danger justify-center bg-white">
                        Falsch werten
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {phase === 'results' && (
          <section className="max-w-4xl mx-auto flex flex-col gap-5">
            <div className="panel-paper p-6 md:p-8 shadow-cardHover">
              <p className="text-xs font-mono uppercase tracking-wider text-sage mb-2">Ergebnis</p>
              <h1 className="plant-name text-3xl md:text-4xl font-bold text-cream">{score}% richtig</h1>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                <div className="rounded-xl bg-white/80 border border-border p-4">
                  <p className="text-xs text-creamDim font-mono uppercase">Richtig</p>
                  <p className="text-2xl font-semibold text-sage">{stats.correct}</p>
                </div>
                <div className="rounded-xl bg-white/80 border border-border p-4">
                  <p className="text-xs text-creamDim font-mono uppercase">Falsch</p>
                  <p className="text-2xl font-semibold text-red-600">{stats.wrong}</p>
                </div>
                <div className="rounded-xl bg-white/80 border border-border p-4">
                  <p className="text-xs text-creamDim font-mono uppercase">Beste Serie</p>
                  <p className="text-2xl font-semibold text-gold">🔥 {stats.bestStreak}</p>
                </div>
                <div className="rounded-xl bg-white/80 border border-border p-4">
                  <p className="text-xs text-creamDim font-mono uppercase">Versuche</p>
                  <p className="text-2xl font-semibold text-cream">{totalAttempts}</p>
                </div>
              </div>
            </div>

            {stats.wrongAnswers.length > 0 && (
              <div className="panel p-5 md:p-6">
                <h2 className="plant-name text-2xl font-bold text-cream mb-4">Falsche Antworten</h2>
                <div className="flex flex-col divide-y divide-border/60">
                  {stats.wrongAnswers.map((item, idx) => (
                    <div key={`${item.plant.id}-${idx}`} className="py-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-2 text-sm">
                      <span className="font-semibold text-cream">{item.plant.german_name}</span>
                      <span className="botanical text-sage">{item.plant.botanical_name}</span>
                      <span className="text-creamDim">Deine Antwort: {item.userAnswer || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button onClick={() => setPhase('setup')} className="btn-ghost justify-center py-3 bg-white/80">
                Zurück zum Setup
              </button>
              <button onClick={() => startSession(eligiblePlants, 50)} className="btn-primary justify-center py-3">
                Neue Session starten
              </button>
            </div>
          </section>
        )}
      </main>
    </>
  );
};
